import { DocumentPreviewHost } from "@/components/documents/DocumentPreviewHost";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { NavLink, Outlet, useLocation } from "react-router";
import { logout } from "@/auth/actions";
import { useMe } from "@/auth/use-me";
import {
  AccessFeaturesProvider,
  useAccessFeatures,
  useHasFeature,
} from "@/auth/access-features";
import {
  AccessPermissionsProvider,
  Permission,
  useHasPermission,
  useHasSystemPermission,
} from "@/auth/access-permissions";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import { useAppName } from "@/lib/use-app-name";
import { useBrandLogo } from "@/lib/use-brand-logo";
import { useHydrateThemeFromSystemSettings } from "@/lib/use-system-theme";
import { AssistantDrawer } from "@/components/chat/AssistantDrawer";
import { AssistantLauncher } from "@/components/chat/AssistantLauncher";
import { ChatDock } from "@/components/chat/ChatDock";
import { MessagesLauncher } from "@/components/chat/MessagesLauncher";
import { Avatar } from "@/components/common/Avatar";
import { MenuItem } from "@/components/design/MenuItem";
import { menuItemClasses } from "@/components/design/menu-item-classes";
import { InviteOutsiderDialog } from "@/components/invite/InviteOutsiderDialog";
import { CompanyContextBar } from "@/components/layout/CompanyContextBar";
import { NotificationCenter } from "@/components/layout/NotificationCenter";
import { TourHintLauncher } from "@/components/layout/TourHintLauncher";
import { SaveBar } from "@/components/layout/SaveBar";
import { SearchBar } from "@/components/layout/SearchBar";
import { SessionExpiredNotice } from "@/components/layout/SessionExpiredNotice";
import { UnsavedGuard } from "@/components/layout/UnsavedGuard";
import { PendingEditsProvider, usePendingEdits } from "@/lib/pending-edits";
import { useApplyThemePrefs } from "@/stores/theme-prefs";

// AppShell - node-by-node port of workshop's components/layout/AppShell.tsx.
// Persistent left sidebar on lg+, mobile drawer below; topbar with the
// inline SearchBar + AccountMenu + Logout; main outlet centred.
//
// ChatDock is not ported yet - Phase 5c reskins the chat module to
// workshop's ChatDock / ChatWindow / MessagesBanner pattern. Until then
// the existing tech-stack ChatOverlay sits in the same DOM position.

// Pillar `label`s are product/brand names — never translated, identical in
// both locales (@adr SPARK-0013-i18n-mandatory-via-react-intl@1 product-name carve-out), so they stay inline literals.
// Submenu items carry a message id + inline default; feature names (Service,
// Community, Pins, Map, Watch, Play) resolve to the same value in en/de, only
// functional chrome (Settings, Questionnaires) actually differs per locale.
interface NavItem {
  to: string;
  labelId: string;
  defaultMessage: string;
  // Genesis 242-access-model feature key gating this page, when it's
  // "active" in FEATURE_CATALOG, undefined for pages with no gate at all
  // (Admin's items, none currently). See plannedFeatureKey for the third
  // case: a key that should exist but doesn't yet.
  featureKey?: string;
  // Inventory only, never read by SidebarNav/RequireFeature. Points at a
  // "planned" FEATURE_CATALOG entry so feature-catalog.test.ts can confirm
  // every page has SOME catalog entry, without prematurely gating a page
  // genesis can't grant yet (that would hide it for everyone, see
  // feature-catalog.ts's module comment).
  plannedFeatureKey?: string;
}

interface Pillar {
  id: string;
  label: string;
  to: string;
  activePath?: string;
  items: readonly NavItem[];
  // Same active/planned split as NavItem.featureKey/plannedFeatureKey, one
  // level up (gating the whole pillar entry point).
  featureKey?: string;
  plannedFeatureKey?: string;
}

const PILLARS: readonly Pillar[] = [
  {
    // Pillar click lands directly on Profile, not the Save dashboard (home dashboard
    // reduction, spark#247) — same "to a subpage, activePath the whole section" shape the
    // ADMIN pillar below already uses, so the sidebar dot/submenu still stay highlighted+open
    // anywhere under /save.
    id: "save",
    label: "SAVE",
    to: "/save/basics",
    activePath: "/save",
    featureKey: FEATURE_CATALOG.save.key,
    items: [
      {
        to: "/save/basics",
        labelId: "nav.profile",
        defaultMessage: "Profile",
        featureKey: FEATURE_CATALOG.saveProfile.key,
      },
      {
        to: "/save/documents",
        labelId: "nav.service",
        defaultMessage: "Documents",
        featureKey: FEATURE_CATALOG.saveDocuments.key,
      },
      {
        to: "/save/contact",
        labelId: "nav.community",
        defaultMessage: "Community",
        featureKey: FEATURE_CATALOG.saveCommunity.key,
      },
      {
        to: "/save/studio",
        labelId: "nav.studio",
        defaultMessage: "Studio",
        featureKey: FEATURE_CATALOG.savePins.key,
      },
    ],
  },
  {
    // The Grow landing chooses Map when available and Assess for play-only audiences.
    id: "grow",
    label: "GROW",
    to: "/grow",
    activePath: "/grow",
    featureKey: FEATURE_CATALOG.grow.key,
    items: [
      {
        to: "/grow/map",
        labelId: "nav.map",
        defaultMessage: "Map",
        featureKey: FEATURE_CATALOG.growMap.key,
      },
      {
        to: "/grow/play",
        labelId: "nav.play",
        defaultMessage: "Play",
        featureKey: FEATURE_CATALOG.growPlay.key,
      },
    ],
  },
  {
    id: "share",
    label: "SHARE",
    to: "/share",
    items: [],
    featureKey: FEATURE_CATALOG.share.key,
  },
  {
    id: "match",
    label: "MATCH",
    to: "/match",
    items: [],
    featureKey: FEATURE_CATALOG.match.key,
  },
  {
    // System-settings surface; links straight to the settings page. Visible
    // only with a system Permission. All functional sections live as tabs
    // inside SettingsPage, not as sidebar submenu entries, see ALL_SECTIONS in
    // SettingsPage.tsx.
    //
    // The pillar stays outside the feature catalog: entering system settings
    // and its administrative actions are Permission-gated.
    id: "admin",
    label: "ADMIN",
    to: "/admin/settings",
    activePath: "/admin",
    items: [],
  },
];

export { PILLARS };

const CHROME_TRANSITION =
  "background-color 1600ms cubic-bezier(0.22, 1, 0.36, 1), " +
  "color 1600ms cubic-bezier(0.22, 1, 0.36, 1), " +
  "border-color 1600ms cubic-bezier(0.22, 1, 0.36, 1)";

function isActivePath(pathname: string, to: string): boolean {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
}

function GearIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.076.124l1.217-.456a1.125 1.125 0 0 1 1.369.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.248a1.125 1.125 0 0 1 1.37-.49l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281Z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
      />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5"
      />
    </svg>
  );
}

// Navigation column shared by the persistent desktop sidebar and the mobile
// drawer. Hover-opened submenus stay open (no collapse on mouse-out) but are
// bound to the current route: after a navigation they reset and only the
// active pillar stays open. Purely derived, no effect needed. `onNavigate`
// lets the mobile drawer close itself when a link is followed.
// ZWEI EINSATZORTE, ZWEI PRÄFIXE: die Navigation steht einmal fest an der Seite
// und einmal in der mobilen Schublade. Ohne Präfix trügen beide dieselben
// Kennungen, und `id` muss im Dokument eindeutig sein
// (UI_CONVENTIONS → Test anchors).
function SidebarNav({
  onNavigate,
  idPrefix,
}: {
  onNavigate?: () => void;
  idPrefix: string;
}) {
  const intl = useIntl();
  const location = useLocation();
  const appName = useAppName();
  const brandLogo = useBrandLogo();
  const hasSystemAccess = useHasSystemPermission();
  const { hasFeature } = useAccessFeatures();
  // The Systemeinstellungen pillar is visible only with a system-settings role.
  // A pillar carrying a featureKey (grow, match) needs that key in the
  // whitelist genesis handed back: deny-by-default, same as everywhere else
  // this model is consulted.
  const pillars = PILLARS.filter((pillar) => {
    if (pillar.id === "admin") return hasSystemAccess;
    if (pillar.featureKey) return hasFeature(pillar.featureKey);
    return true;
  });
  const [opened, setOpened] = useState<{ path: string; ids: Set<string> }>(
    () => ({
      path: location.pathname,
      ids: new Set<string>(),
    }),
  );
  const openIds = opened.path === location.pathname ? opened.ids : null;

  return (
    <>
      <NavLink
        id={`${idPrefix}-home`}
        to="/"
        onClick={() => {
          setOpened({ path: "/", ids: new Set<string>() });
          onNavigate?.();
        }}
        // The /tour-all intro points at the logo as "the way to the main page".
        data-assistant-anchor="nav.home"
        className="px-5 pb-8 pt-6 font-serif text-smycles-display-s leading-none tracking-tight text-ink"
      >
        {brandLogo ? (
          // The uploaded platform logo (Branding, spark#314) stands in for the name. The name stays
          // the accessible one: a wordmark is an image of exactly these words, so `alt` carries
          // them and the link keeps the name it had for the tour, the tests and a screen reader.
          // Bounded in both directions, left-aligned: a wide mark fills the column, a square one
          // sits where the text used to start, and neither may grow the sidebar.
          <img
            src={brandLogo}
            alt={appName}
            className="block h-10 w-auto max-w-[160px] object-contain object-left"
          />
        ) : (
          appName
        )}
      </NavLink>
      <nav
        id={idPrefix}
        aria-label={intl.formatMessage({
          id: "nav.mainLandmark",
          defaultMessage: "Main",
        })}
        className="flex-1 space-y-1 overflow-y-auto px-3"
      >
        {pillars.map((pillar) => {
          const active = isActivePath(
            location.pathname,
            pillar.activePath ?? pillar.to,
          );
          const open = active || (openIds?.has(pillar.id) ?? false);
          return (
            <div
              key={pillar.id}
              onMouseEnter={
                pillar.items.length > 0
                  ? () =>
                      setOpened((prev) => {
                        const base =
                          prev.path === location.pathname
                            ? prev.ids
                            : new Set<string>();
                        if (base.has(pillar.id)) return prev;
                        return {
                          path: location.pathname,
                          ids: new Set(base).add(pillar.id),
                        };
                      })
                  : undefined
              }
            >
              <NavLink
                id={`${idPrefix}-${pillar.id}`}
                to={pillar.to}
                onClick={onNavigate}
                // Assistant onboarding anchor (/tour-all pillar intro highlights + lasting marker).
                data-assistant-anchor={`nav.${pillar.id}`}
                className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-smycles-body uppercase tracking-[0.18em] transition ${
                  active
                    ? "font-medium text-ink"
                    : "text-ink-muted hover:font-medium hover:text-ink"
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                    active ? "bg-ink" : "bg-transparent"
                  }`}
                />
                <span>{pillar.label}</span>
              </NavLink>
              {pillar.items.length > 0 ? (
                <div
                  className={`grid transition-[grid-template-rows] duration-[450ms] ease-out ${
                    open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div
                      // Außerhalb von `nav-…`: die Navigationsreihenfolge (SMY-0487) liest
                      // jede Kennung mit diesem Präfix als Eintrag.
                      id={`sub${idPrefix}-${pillar.id}`}
                      className={`mb-1 ml-7 mt-1 space-y-0.5 border-l border-ink/10 pl-3 transition-opacity duration-[450ms] ease-out ${
                        open ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      {pillar.items
                        .filter((item) => !item.featureKey || hasFeature(item.featureKey))
                        .map((item) => (
                        <NavLink
                          key={item.to}
                          // Der Unterpunkt trägt seinen technischen Schlüssel
                          // (nav.profile → profile), nicht seine Beschriftung.
                          id={`${idPrefix}-${item.labelId.replace(/^nav\./, "")}`}
                          to={item.to}
                          onClick={onNavigate}
                          className={({ isActive }) =>
                            `block rounded-md px-3 py-2 text-left text-smycles-body transition ${
                              isActive
                                ? "font-medium text-ink"
                                : "text-ink-muted hover:font-medium hover:text-ink"
                            }`
                          }
                        >
                          {intl.formatMessage({
                            id: item.labelId,
                            defaultMessage: item.defaultMessage,
                          })}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>
    </>
  );
}

// Topbar account: shows just the first name + avatar; hover/focus opens a
// card with full name, email, Settings, and Logout. Name + photo come from the
// real Person record (useMe), not the JWT claims. The pt-1 on the card wrapper
// bridges the gap to the trigger so hover doesn't tear off when crossing.
//
// The account card is also where inviting an outsider lives (#183). It is a
// rare, deliberate act by a small Permission-holding group, so it stays out of the
// topbar icon row: what sits there is permanently present and competes with
// everyday actions. A menu row costs no chrome and, when the Permission is missing,
// leaves no gap behind.
//
//
// WHY THE COMPANY ENTRIES LIVE HERE. Both are account-scoped, which is what this
// menu already holds: "my companies" is the set of company_membership rows that
// point at ME, and the create:company gate is a Genesis Permission of the USER, never
// on a company (the company account model, level A). It is
// also where the account menu exposes other personal memberships.
//
// The gate copies the existing Permission-gated entry point: the ADMIN pillar is
// filtered out of SidebarNav unless the user holds a system Permission, and
// SettingsPage filters its section list the same way. A new
// top-level pillar was the alternative and was not taken — that would make
// companies a fifth product pillar next to SAVE/GROW/SHARE/MATCH, an information-
// architecture decision no ADR in the epic makes.
//
// Presentation only, like every Permission check in spark: genesis re-checks
// create:company against principal_grants when createCompany runs, and
// /c/new is guarded by requireCompanyCreation regardless of what is
// rendered here.
export function AccountMenu() {
  const intl = useIntl();
  const me = useMe();
  const canInviteOutsider = useHasPermission(Permission.ProvisionInvitee);
  const [inviteOpen, setInviteOpen] = useState(false);
  const canCreateCompany = useHasPermission(Permission.CreateCompany);
  const accountLabel = intl.formatMessage({
    id: "account.label",
    defaultMessage: "Account",
  });
  const firstName = me.firstName || accountLabel;
  const fullName = me.fullName || firstName;
  const person = {
    name: fullName,
    avatarContent: me.avatarContent ?? null,
    avatarMime: me.avatarMime ?? null,
  };

  return (
    <div id="account-menu" className="group relative">
      <button
        id="account-menu-trigger"
        type="button"
        className="flex max-w-[160px] items-center gap-2 rounded-full py-1 pl-1 pr-2.5 text-smycles-body text-ink transition hover:bg-ink/[0.06] group-focus-within:bg-ink/[0.06]"
      >
        <Avatar person={person} size="sm" />
        <span className="truncate">{firstName}</span>
      </button>

      <div className="invisible absolute right-0 top-full z-40 w-64 translate-y-1 pt-1 opacity-0 transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
        <div className="rounded-md bg-surface p-4 shadow-lg ring-1 ring-ink/10">
          <NavLink
            id="account-menu-profile"
            to="/save/basics"
            className="-m-2 flex items-center gap-3 rounded-md p-2 transition hover:bg-ink/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/15"
          >
            <Avatar person={person} size="lg" />
            <div className="min-w-0">
              <div className="truncate text-smycles-body font-medium text-ink">
                {fullName}
              </div>
              {me.email ? (
                <div className="break-all text-smycles-meta leading-snug text-ink-muted">
                  {me.email}
                </div>
              ) : null}
            </div>
          </NavLink>

          <div className="mt-3 border-t border-ink/5 pt-2">
            <NavLink id="account-menu-companies" to="/c" className={menuItemClasses()}>
              <span className="shrink-0">
                <CompanyIcon />
              </span>
              <span className="min-w-0 flex-1 truncate">
                <FormattedMessage
                  id="nav.myCompanies"
                  defaultMessage="My companies"
                />
              </span>
            </NavLink>
            {canCreateCompany ? (
              <NavLink id="account-menu-new-company" to="/c/new" className={menuItemClasses()}>
                <span className="shrink-0">
                  <PlusIcon />
                </span>
                <span className="min-w-0 flex-1 truncate">
                  <FormattedMessage
                    id="nav.newCompany"
                    defaultMessage="New company"
                  />
                </span>
              </NavLink>
            ) : null}
            <NavLink id="account-menu-settings" to="/settings" className={menuItemClasses()}>
              <span className="shrink-0">
                <GearIcon />
              </span>
              <span className="min-w-0 flex-1 truncate">
                <FormattedMessage id="nav.settings" defaultMessage="Settings" />
              </span>
            </NavLink>
            {canInviteOutsider ? (
              <MenuItem
                id="account-menu-invite"
                icon={<EnvelopeIcon />}
                onClick={() => setInviteOpen(true)}
              >
                <FormattedMessage
                  id="nav.inviteOutsider"
                  defaultMessage="Invite external person"
                />
              </MenuItem>
            ) : null}
            <button
              id="account-menu-logout"
              type="button"
              onClick={() => void logout()}
              className={menuItemClasses()}
            >
              <span className="shrink-0">
                <LogoutIcon />
              </span>
              <span className="min-w-0 flex-1 truncate text-left">
                <FormattedMessage id="nav.logout" defaultMessage="Logout" />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Portalled to <body>: the card above is only visible while hovered
          (group-hover), which would take a nested dialog with it, and the
          topbar's `relative z-30` header would trap the modal below the
          SaveBar (z-900). Rendered only for Permission holders, so there is
          no reachable path to the form without it. */}
      {canInviteOutsider && inviteOpen
        ? createPortal(
            <InviteOutsiderDialog open onClose={() => setInviteOpen(false)} />,
            document.body,
          )
        : null}
    </div>
  );
}

function CompanyIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 21h16.5M4.5 3h9.75c.414 0 .75.336.75.75V21H3.75V3.75c0-.414.336-.75.75-.75Zm10.5 6.75h4.5c.414 0 .75.336.75.75V21h-5.25V9.75ZM6.75 6.75h3m-3 3.75h3m-3 3.75h3"
      />
    </svg>
  );
}

function EnvelopeIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4.5v15m7.5-7.5h-15"
      />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75"
      />
    </svg>
  );
}

/**
 * The page's scroll container, which reserves room for the SaveBar while it is up.
 *
 * <p>SaveBar is `fixed` and therefore out of flow: it floats OVER this element rather than pushing
 * it, so whatever sat at the bottom of a scrolled page — an "Add to company" button, the last row of
 * a list — ended up underneath the pill with no way to scroll it clear. Padding the scroll container
 * gives that content somewhere to go.
 *
 * <p>Only while a scope the bar saves is dirty — `dirty && !ownSaveAction`, the predicate SaveBar
 * filters with — read from the same registry the bar reads, so the two never disagree about whether
 * the bar is on screen. The value clears the bar's own geometry: it sits
 * `bottom-4` (1rem) with a `py-3` pill of about 3rem, so 5rem leaves a rem of air, and `sm:bottom-8`
 * needs one more.
 *
 * <p>Its own component because AppShell renders the PROVIDER — a hook call in AppShell's body would
 * run outside the context it is trying to read.
 */
function ScrollArea({ children }: { children: ReactNode }) {
  const ctx = usePendingEdits();
  const dirty = Object.values(ctx?.scopes ?? {}).some((scope) => scope.dirty && !scope.ownSaveAction);

  return (
    <main
      // `relative` ist hier kein Styling, sondern die Clipping-Zusicherung: ohne eigenen
      // containing block klammern sich absolut positionierte Nachkommen an den naechsten
      // positionierten Vorfahren OBERHALB dieses Containers und entkommen damit dem
      // overflow-auto. Ein einziges `sr-only` (Tailwind: position absolute) genuegt dann, um
      // das Dokument neben diesem Container ein zweites Mal scrollbar zu machen.
      className={`@container relative min-h-0 flex-1 overflow-auto${dirty ? " pb-20 sm:pb-24" : ""}`}
    >
      {children}
    </main>
  );
}

export function AppShell() {
  return (
    <AccessFeaturesProvider>
      <AccessPermissionsProvider>
        <AppShellChrome />
      </AccessPermissionsProvider>
    </AccessFeaturesProvider>
  );
}

// Split out so useHasFeature/useHasPermission (below) have a provider above
// them: AppShell itself can't call either, it renders the providers.
function AppShellChrome() {
  const intl = useIntl();
  const hasSearchFeature = useHasFeature(FEATURE_CATALOG.search.key);
  const hasAssistantFeature = useHasFeature(FEATURE_CATALOG.assistant.key);
  const hasChatFeature = useHasFeature(FEATURE_CATALOG.chat.key);

  useApplyThemePrefs();
  useHydrateThemeFromSystemSettings();

  // Mobile navigation: under lg the sidebar is hidden and the hamburger
  // opens it as a drawer. Each drawer link closes via onNavigate; the
  // overlay (z-50) covers all other nav triggers, so closing on navigate
  // is enough. Escape also closes.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = () => setMobileNavOpen(false);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileNavOpen]);

  return (
    <PendingEditsProvider>
      <div
        data-theme-mode="light"
        data-depth="off"
        className="flex h-screen bg-page text-ink"
        style={{ transition: CHROME_TRANSITION }}
      >
        {/* Persistent sidebar from lg+ - below that the drawer takes over. */}
        <aside className="hidden w-[200px] shrink-0 flex-col lg:flex">
          <SidebarNav idPrefix="nav" />
        </aside>

        {/* Mobile drawer + backdrop. Stays mounted for the slide animation;
            without visibility it doesn't catch clicks (pointer-events-none). */}
        <div
          className={`fixed inset-0 z-50 lg:hidden ${mobileNavOpen ? "" : "pointer-events-none"}`}
          aria-hidden={!mobileNavOpen}
        >
          <div
            onClick={closeMobileNav}
            className={`absolute inset-0 bg-ink/30 transition-opacity duration-300 ${
              mobileNavOpen ? "opacity-100" : "opacity-0"
            }`}
          />
          <aside
            className={`absolute inset-y-0 left-0 flex w-[260px] max-w-[80%] flex-col bg-page shadow-xl transition-transform duration-300 ease-out ${
              mobileNavOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <SidebarNav idPrefix="nav-mobil" onNavigate={closeMobileNav} />
          </aside>
        </div>

        <div className="relative flex min-w-0 flex-1 flex-col">
          <header
            className="relative z-30 bg-page"
            style={{ transition: CHROME_TRANSITION }}
          >
            <div className="flex h-14 items-center gap-3 px-4 sm:gap-6 sm:px-6">
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                aria-label={intl.formatMessage({
                  id: "nav.openMenu",
                  defaultMessage: "Open menu",
                })}
                className="-ml-1 shrink-0 rounded-md p-2 text-ink transition hover:bg-ink/[0.06] hover:text-ink lg:hidden"
              >
                <MenuIcon />
              </button>
              <div className="flex min-w-0 flex-1 justify-center">
                {/* data-assistant-anchor: the /tour-all intro presents the topbar items. */}
                {hasSearchFeature ? (
                  <div
                    className="w-full max-w-[520px]"
                    data-assistant-anchor="topbar.search"
                  >
                    <SearchBar />
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {hasAssistantFeature ? (
                  <span
                    className="inline-flex"
                    data-assistant-anchor="topbar.assistant"
                  >
                    <AssistantLauncher />
                  </span>
                ) : null}
                {hasChatFeature ? (
                  <span
                    className="inline-flex"
                    data-assistant-anchor="topbar.chat"
                  >
                    <MessagesLauncher />
                  </span>
                ) : null}
                <span
                  className="inline-flex"
                  data-assistant-anchor="topbar.notifications"
                >
                  <NotificationCenter />
                </span>
                <span
                  className="inline-flex"
                  data-assistant-anchor="topbar.help"
                >
                  <TourHintLauncher />
                </span>
                <span
                  className="inline-flex"
                  data-assistant-anchor="topbar.user"
                >
                  <AccountMenu />
                </span>
              </div>
            </div>
            {/* The acting-company statement, below the topbar and above the page: the mode is true
                of the session rather than of the route, so it belongs to the chrome. Renders
                nothing at all when the person is acting as themselves, which is almost always. */}
            <CompanyContextBar />
          </header>
          <ScrollArea>
            <Outlet />
          </ScrollArea>
          <ChatDock />
        </div>
        <AssistantDrawer />
        <SaveBar />
        <UnsavedGuard />
        <SessionExpiredNotice />
        <DocumentPreviewHost />
      </div>
    </PendingEditsProvider>
  );
}
